import React, { useRef } from "react";
import ForceGraph3D from "3d-force-graph";
import { useMount } from "react-use";
import styles from "./LOGraph.module.css";
import LOData from "../assets/LOGraphData.json";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";
import SpriteText from "three-spritetext";
import {
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector2,
  CanvasTexture,
  CubeTexture,
} from "three";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function findNearestNeighbours(nodeId: string) {
  const neighbourLinks = LOData.links.filter((l) => {
    return (
      l.source === nodeId ||
      l.target === nodeId ||
      //@ts-expect-error id
      l.source.id === nodeId ||
      //@ts-expect-error id
      l.target.id === nodeId
    );
  });
  const neighbourNodes = LOData.nodes.filter((n) =>
    neighbourLinks.find(
      (link) =>
        link.source === n.id ||
        link.target === n.id ||
        //@ts-expect-error id
        link.source.id === n.id ||
        //@ts-expect-error id
        link.target.id === n.id
    )
  );
  console.log({
    nodes: neighbourNodes,
    links: neighbourLinks,
  });
  return {
    nodes: neighbourNodes,
    links: neighbourLinks,
  };
}

const PROBLEM_STRATEGY = [
  {
    "strategy_step": "Compute derivative: f'(x) = 4x + 8",
    "learning_objectives": new Set([
      "definition_of_the_derivative",
    ].flatMap(lo => [lo, ...findNearestNeighbours(lo).nodes.map(n => n.id)])),
    "color": "#39FF14",
  },
  {
    "strategy_step": "Find zeros of the derivative by solving 4x + 8 = 0, yielding x = -2",
    "learning_objectives": new Set([
      "apply_chain_rule_derivative",
      "apply_chain_rule_product_quotient_rules_derivative",
      "apply_power_rule_chain_rule_derivative",
      "apply_chain_rule_finding_derivative_function_involving_log_piece",
      "apply_constant_multiple_rule_finding_derivative",
      "derivative_of_sum_difference_and_product_with_a_constant",
      "apply_derivative_each_side_equation",
      "determine_derivative_implicit_differentiation_power_product_quotient_chain_rules",
      "compute_derivative_of_function_using_differentiation_rules",
      "determine_derivative_function_a_exp",
      "derivative_real_world_application_derivative_rules",
      "compute_instantaneous_rate_change_of_function",
      "define_der_limit_h_approaches_0",
      "der_radical_function_using_limit_definition_derivative",
      "write_derivatives_using_notation",
      "derivative_chain_rule_function_derivatives_defined_table_values",
      "derivative_chain_rule_function_derivatives_given_graphs",
      "derivatives_of_power_functions",
      "derivatives_of_polynomials",
      "write_formula_derivative_given_starting_functions_defined_formula",
      "der_rational_function_using_limit_definition_derivative",
      "explain_number_defined_limit_definition_derivative",
      "determine_derivative_function_applying_combination_sum_difference_power_product_quotient_rules",
      "determine_der_point_function_limit_definition_derivative_x_to_a",
      "determine_instantaneous_rate_change_limit_definition_derivative_delta_approaches",
      "determine_instantaneous_velocity_given_position_function_moment_time",
      "determine_slope_function_point_difference_quotient_notation",
      "write_chain_rule_prime_leibniz_notation",
      "interpret_leibniz_notation"
    ].flatMap(lo => [lo, ...findNearestNeighbours(lo).nodes.map(n => n.id)])),
    "color": "#00FF66",
  },
  {
    "strategy_step": "Identify candidate points for extrema by including the critical point x = -2 and the domain endpoints x = -3 and x = 1",
    "learning_objectives": new Set([
      "extreme_value_theorem",
      "absolute_extrema",
      "absolute_min_max_function",
      "critical_numbers",
    ].flatMap(lo => [lo, ...findNearestNeighbours(lo).nodes.map(n => n.id)])),
    "color": "#FF1493",
  },
  {
    "strategy_step": "Evaluate the function at x = -3, -2, and 1 to obtain f(-3) = -8, f(-2) = -6, and f(1) = 10",
    "learning_objectives": new Set([
      "evaluate_polynomial_for_given_value",
      "order_decimals_and_fractions",
      "add_integers",
      "multiply_integers",
      "substitute_expression_for_variable_into_expression",
      "evaluate_function",
      "evaluate_linear_function_at_given_value",
      "substitute_numerical_value_for_variable_into_expression",
      "solve_quadratic_equations_by_zero_product_property",
      "given_value_of_linear_function_find_x_value",
      "find_zeros_rational_function",
      "solve_linear_equation_using_general_strategy",
      "determine_if_number_is_solution_to_equation",
      "determine_whether_number_solution_equation",
      "find_zeros_of_a_polynomial_function",
      "simplify_and_solve_equation_using_division_and_multiplication_properties_of_equality",
      "solve_equations_with_polynomial_functions",
      "solving_quadratic_equation_by_factoring_when_the_leading_coefficient_is_one",
      "solve_quadratic_equations_of_the_form_ax_2_equals_k_using_square_root_property",
      "solve_equation_for_variable_using_the_division_property_of_equality_and_check_solution",
      "simplify_and_solve_equation_using_subtraction_and_addition_properties_of_equality",
      "solve_equation_for_variable_using_multiplication_property_of_equality_and_check_solution",
      "solve_equation_with_constants_on_both_sides",
      "solve_equation_using_division_and_multiplication_properties_of_equality",
      "solve_equation_with_variables_on_both_sides",
      "solve_equation_with_variables_and_constants_on_both_sides",
      "solve_linear_equation_with_integer_solutions",
      "solve_linear_equation_with_rational_solutions",
      "solve_quadratic_equation_for_application_where_only_less_a_solution_is_viable",
      "solve_quadratic_equation_for_application_where_only_negative_solution_is_viable"
    ].flatMap(lo => [lo, ...findNearestNeighbours(lo).nodes.map(n => n.id)])),
    "color": "#FFD700",
  },
  {
    "strategy_step": "Conclude that the minimum value of f(x) is -8, which occurs at x = -3",
    "learning_objectives": new Set([
      "determine_minimum_maximum_value_of_a_quadratic_function"
    ].flatMap(lo => [lo, ...findNearestNeighbours(lo).nodes.map(n => n.id)])),
    "color": "#FF6B6B",
  },
];

const SOLUTION_STEPS = [
  {
    "step": "Expressing the derivative",
    "transformation": "f'(x) = (2x^2)' + (8x)'",
    "learning_objective": "write_derivatives_using_notation"
  },
  {
    "step": "Computing the derivative",
    "transformation": "f'(x) = 4x + 8",
    "learning_objective": "apply_power_rule_chain_rule_derivative"
  },
  {
    "step": "Finding critical points",
    "transformation": "Set f'(x) = 0: 4x + 8 = 0, hence x = -2; domain endpoints: x = -3 and x = 1",
    "learning_objective": "critical_numbers"
  },
  {
    "step": "Evaluating function values",
    "transformation": "f(-3) = -6, f(-2) = -8, f(1) = 10",
    "learning_objective": "evaluate_polynomial_for_given_value"
  },
  {
    "step": "Determining the absolute minimum",
    "transformation": "The minimum value is -8 at x = -2",
    "learning_objective": "closed_interval_method"
  }
];

const DEFAULT_COLOR = "#56EEF4";

const NODES_BY_ID = LOData.nodes.reduce((acc, node) => {
  acc[node.id] = node;
  return acc;
}, {} as Record<string, typeof LOData.nodes[0]>);

export const LOGraph = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // refs and state for SOLUTION_STEPS playback
  const graphRef = useRef<typeof ForceGraph3D | null>(null);
  const nodeMeshRefs = useRef<
    Record<
      string,
      { outer: Mesh; inner: Mesh; originalColor: string }
    >
  >({});
  const prevHighlightedRef = useRef<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const [highlightedLearningObjective, setHighlightedLearningObjective] = React.useState<string | null>(
    SOLUTION_STEPS[0].learning_objective
  );

  const [isPlaying, setIsPlaying] = React.useState(false);

  const nextStep = (prev: number) => {
    let nextIndex = prev === SOLUTION_STEPS.length ? 0 : (prev + 1);
    if (nextIndex === SOLUTION_STEPS.length) {
      setHighlightedLearningObjective(null);
    } else {
      setHighlightedLearningObjective(SOLUTION_STEPS[nextIndex].learning_objective);
    }
    return nextIndex;
  };

  const prevStep = (prev: number) => {
    let nextIndex = prev === 0 ? SOLUTION_STEPS.length : (prev - 1);
    if (nextIndex === SOLUTION_STEPS.length) {
      setHighlightedLearningObjective(null);
    } else {
      setHighlightedLearningObjective(SOLUTION_STEPS[nextIndex].learning_objective);
    }
    return nextIndex;
  };

  // Setup the step cycle – every 4000ms cycle to the next SOLUTION_STEP when playing
  React.useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentStepIndex(nextStep);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  // When the highlighted learning objective changes,
  // update the node colors and focus the camera.
  React.useEffect(() => {
    if (!graphRef.current) return;
    // Update new highlighted node color and focus camera
    if (highlightedLearningObjective && nodeMeshRefs.current[highlightedLearningObjective]) {
      const { outer } = nodeMeshRefs.current[highlightedLearningObjective];

      // Focus camera on the highlighted node (using its current position)
      const { x, y, z } = outer.position;
      const distance = 100;
      const distRatio = 1 + distance / Math.hypot(x, y, z);
      const newPos = { x: x * distRatio, y: y * distRatio, z: z * distRatio };
      // @ts-ignore
      graphRef.current?.cameraPosition(newPos, { x, y, z }, 1500);
    } else {
      // @ts-ignore
      graphRef.current?.cameraPosition({ x: 1414, y: 0, z: 1414 }, { x: 0, y: 0, z: 0 }, 4000);
    }
    prevHighlightedRef.current = highlightedLearningObjective;
  }, [highlightedLearningObjective]);

  useMount(() => {
    if (containerRef.current) {
      // const data = { ...findNearestNeighbours(LOData.nodes[0].id) };
      const data = LOData;
      const graph = new ForceGraph3D(containerRef.current)
        .graphData(data)
        .backgroundColor("black")
        .linkMaterial(() => {
          return new MeshBasicMaterial({
            color: "#2e5ca6",
            opacity: 0.1,
            // transparent: true,
          });
        })
        .nodeThreeObject((node) => {
          // Compute the original color from PROBLEM_STRATEGY or use DEFAULT_COLOR:
          const originalColor =
            PROBLEM_STRATEGY.find((s) =>
              s.learning_objectives.has(node.id!.toString())
            )?.color || DEFAULT_COLOR;
          const geometry = new SphereGeometry(3);
          const innerGeometry = new SphereGeometry(1.5);
          const sprite = new SpriteText(NODES_BY_ID[node.id!].name as string, 3);
          sprite.translateY(-5);
          sprite.backgroundColor = 'rgba(0,0,0,0.7)';
          sprite.strokeColor = 'rgba(0,0,0,0.7)';
          sprite.strokeWidth = 2;
          const material = new MeshBasicMaterial({
            color: originalColor,
            opacity: 0.8,
            transparent: true,
          });
          const innerMaterial = new MeshBasicMaterial({
            color: originalColor,
            opacity: 1,
            transparent: true,
          });

          const outerMesh = new Mesh(geometry, material);
          const innerMesh = new Mesh(innerGeometry, innerMaterial);
          outerMesh.add(innerMesh).add(sprite);

          // Save a reference to update colors later
          nodeMeshRefs.current[node.id!] = {
            outer: outerMesh,
            inner: innerMesh,
            originalColor: originalColor,
          };
          return outerMesh;
        })
        .onNodeClick((node) => {
          const distance = 40;
          const distRatio =
            1 + distance / Math.hypot(node.x!, node.y!, node.z!);

          const newPos =
            node.x && node.y && node.z
              ? {
                  x: node.x * distRatio,
                  y: node.y * distRatio,
                  z: node.z * distRatio,
                }
              : { x: 0, y: 0, z: distance };

          graph.cameraPosition(
            newPos,
            {
              x: node.x ?? 0,
              y: node.y ?? 0,
              z: node.z ?? 0,
            },
            1500
          );
        });

      const resolution = new Vector2(10, 10);

      const bloomPass = new UnrealBloomPass(resolution, 1, 0.75, 0);
      graph.postProcessingComposer().addPass(bloomPass);
      graph.d3Force("link");
      graph.showNavInfo(false);
      // Функция для создания текстуры сетки
      const createGridTexture = (size = 4096, divisions = 200) => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d")!;

        ctx.fillStyle = "#000000"; // Цвет фона
        ctx.fillRect(0, 0, size, size);

        ctx.strokeStyle = "#180E4E"; // Цвет линий сетки
        ctx.lineWidth = 0.5;

        for (let i = 0; i <= divisions; i++) {
          const pos = (size / divisions) * i;

          // Горизонтальные линии
          ctx.beginPath();
          ctx.moveTo(0, pos);
          ctx.lineTo(size, pos);
          ctx.stroke();

          // Вертикальные линии
          ctx.beginPath();
          ctx.moveTo(pos, 0);
          ctx.lineTo(pos, size);
          ctx.stroke();
        }

        return new CanvasTexture(canvas);
      };

      // Создаём массив текстур для шести сторон куба
      const gridTextures = Array.from({ length: 6 }).map(() =>
        createGridTexture()
      );

      // Создаём кубическую текстуру
      const cubeTexture = new CubeTexture(gridTextures.map((t) => t.image));
      cubeTexture.needsUpdate = true;

      // Устанавливаем фон сцены
      const scene = graph.scene();
      scene.background = cubeTexture;

      graph.cameraPosition(
        {
          x: 0,
          y: 0,
          z: 100,
        },
        {
          x: 0,
          y: 0,
          z: 0,
        },
        1500
      );

      // Save the graph instance in a ref for later access.
      // @ts-ignore
      graphRef.current = graph;
      setIsPlaying(true);
    }
  });

  return (
    <div className={styles.container}>
      <div className={styles.playControls}>
        <button
          className={styles.playButton}
          onClick={() => setCurrentStepIndex(prevStep)}
        >
          ⏮
        </button>
        <button
          className={styles.playButton}
          onClick={() => setIsPlaying(!isPlaying)}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button
          className={styles.playButton}
          onClick={() => setCurrentStepIndex(nextStep)}
        >
          ⏭
        </button>
      </div>

      {/* Overlay for displaying the current SOLUTION_STEP */}
      <div className={styles.solutionStepOverlay}>
        {highlightedLearningObjective ? (
          <>
            <p>
              <strong>{currentStepIndex + 1}. {SOLUTION_STEPS[currentStepIndex].step}</strong>
            </p>
            <p>
              {currentStepIndex === SOLUTION_STEPS.length - 1 && <em>Answer: <br /></em>}
              {SOLUTION_STEPS[currentStepIndex].transformation}
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>Student is solving the following problem:</strong>
            </p>
            <p>
              Find the minimum value of f(x) = 2x² + 8x on [-3, 1]
            </p>
          </>
        )}
      </div>

      <div className={styles.LOGraph} ref={containerRef} />
    </div>
  );
};
